import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Form, Input, Button } from "antd";
import {
  LockOutlined,
  TeamOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import SingleLogo from "../assets/images/single-logo.svg";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import Loader from "../components/Loader";

const ForgetPassword = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { request } = useRequest();

  const adminId = searchParams.get("adminId");
  const token = searchParams.get("token");

  const onFinish = (values) => {
    const { newPassword } = values;

    if (!adminId) {
      ShowToast("Missing adminId in URL", Severty.ERROR);
      return;
    }

    setLoading(true);

    request({
      url: `/admin/auth/reset-password`,
      method: "POST",
      data: { adminId, password: newPassword, token },
      onSuccess: (data) => {
        setLoading(false);
        ShowToast(data.message || "Password reset successful", Severty.SUCCESS);
        navigate("/login");
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error?.response?.data?.message || "Failed to reset password", Severty.ERROR);
      },
    });
  };

  return (
    <>
      {loading && <Loader />}

      <div className="admin-login-shell">
        {/* ── Left: form panel ── */}
        <div className="admin-login-form-panel">
          <div className="admin-login-form-inner">
            <div className="admin-login-logo">
              <img src={SingleLogo} alt="Imergey" />
              <span className="admin-login-badge">Admin</span>
            </div>

            <h1 className="admin-login-heading">Reset Password</h1>
            <p className="admin-login-sub">Enter a new password for your admin account.</p>

            <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              className="admin-login-form"
            >
              <Form.Item
                label="New Password"
                name="newPassword"
                rules={[
                  { required: true, message: "Please enter a new password" },
                  { min: 8, message: "Password must be at least 8 characters long!" },
                  { max: 100, message: "Password cannot exceed 100 characters" },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
                    message: "Password must contain uppercase, lowercase, number, and special character!",
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="admin-input-icon" />}
                  placeholder="New password"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={["newPassword"]}
                rules={[
                  { required: true, message: "Please confirm your password" },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === "") {
                        return Promise.reject(new Error("Confirm Password cannot be only spaces"));
                      }
                      if (form.getFieldValue("newPassword") !== value) {
                        return Promise.reject(new Error("Passwords do not match"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="admin-input-icon" />}
                  placeholder="Confirm password"
                  size="large"
                />
              </Form.Item>

              <Form.Item style={{ marginTop: 8 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  className="admin-login-btn"
                  loading={loading}
                >
                  Reset Password
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>

        {/* ── Right: brand panel ── */}
        <div className="admin-login-brand-panel">
          <div className="admin-brand-glow admin-brand-glow--1" />
          <div className="admin-brand-glow admin-brand-glow--2" />

          <div className="admin-brand-content">
            <div className="admin-brand-logo-wrap">
              <img src={SingleLogo} alt="Imergey" className="admin-brand-logo" />
              <span className="admin-brand-name">IMERGEY</span>
            </div>
            <h2 className="admin-brand-headline">Command your<br />brand universe</h2>
            <p className="admin-brand-tagline">
              Manage clients, teams, content, and growth — all from one powerful admin hub.
            </p>

            <div className="admin-brand-stats">
              <div className="admin-stat-card">
                <TeamOutlined className="admin-stat-icon" />
                <div>
                  <div className="admin-stat-value">Multi-team</div>
                  <div className="admin-stat-label">Role management</div>
                </div>
              </div>
              <div className="admin-stat-card">
                <BarChartOutlined className="admin-stat-icon" />
                <div>
                  <div className="admin-stat-value">Real-time</div>
                  <div className="admin-stat-label">Analytics &amp; reports</div>
                </div>
              </div>
              <div className="admin-stat-card">
                <SafetyCertificateOutlined className="admin-stat-icon" />
                <div>
                  <div className="admin-stat-value">Secure</div>
                  <div className="admin-stat-label">Access control</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgetPassword;
